/**
 * LessonLeft Component
 *
 * Main container for lesson content, navigation, and view management in the educational platform.
 * Handles tab navigation, video, quiz, activity, and peer evaluation views, integrating with lesson-specific data and user actions.
 *
 * Features:
 * - Manages navigation between lesson sections (video, quiz, activity, peer evaluation)
 * - Handles URL changes and view state for deep-linking and browser navigation
 * - Renders child components for each section, passing necessary props and context
 * - Integrates with Material-UI for responsive UI elements
 * - Supports audio and video lesson formats
 * - Tracks user progress and media history for lesson resumption
 * - Handles conditional rendering for finished course messages and loading states
 *
 * URL Structure Expected: /{academy}/{section}/{idLesson}/{idCourse}
 * - academy: Academy identifier (e.g., 'mayahii', 'edustore')
 * - section: Section identifier ('f', 'aud', 'q', 'ac', 'coe', 'lesson', etc.)
 * - idLesson: Lesson identifier
 * - idCourse: Course identifier
 *
 * @param {Object} props - Component properties
 * @param {Object} [props.lessonInfo] - Lesson data object
 * @param {number} [props.idCourse] - Course ID for lesson context
 * @param {number} [props.idLesson] - Lesson ID for lesson context
 * @param {number} [props.view] - Current view index (1: video, 2: quiz, 3: activity, 4: peer evaluation)
 * @param {function} [props.changeView] - Function to change the current view
 * @param {function} [props.changeVideoStatus] - Function to change video status
 * @param {function} [props.changeQuizStatus] - Function to change quiz status
 * @param {function} [props.changeHomeworkStatus] - Function to change homework status
 * @param {function} [props.changeCoevaluationStatus] - Function to change coevaluation status
 * @param {function} [props.loadLesson] - Function to load lesson data
 * @param {function} [props.transcriptHandler] - Function to handle transcript updates
 * @param {function} [props.playerTimeHandler] - Function to handle player time updates
 * @param {number} [props.historyMedia] - Last media history position for resuming video/audio
 * @param {string} [props.format] - Format of the lesson (e.g., 'AUDIO', 'VIDEO')
 * @returns {JSX.Element} Rendered LessonLeft component with navigation and lesson content
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import BtnsLessons from './BtnsLessons/BtnsLessons';
import VideoLesson from './VideoLesson/VideoLesson';
import Activity from './Activity/Activity';
import QuizContainer from './QuizContainer/QuizContainer';
import CoevaluationUsersContainer from './CoevaluationUsersContainer/CoevaluationUsersContainer';
import FinishedCourseMessage from './FinishedCourseMessage/FinishedCourseMessage';

import classes from './LessonLeft.module.css';
const LessonLeft = props => {
  // URL parameters extraction for lesson navigation and context
  const url = window.location.pathname.split('/');
  const [showMessage] = useState(false); // Flag for finished course message
  const { t } = useTranslation(['lesson']);

  /**
   * Handles tab navigation and updates URL accordingly
   * Updates browser history for deep-linking and navigation
   * @param {object} event - Tab change event
   * @param {number} newValue - New tab index
   */
  const handleChange = (event, newValue) => {
    props.changeView(newValue);
    let section = '';
    if (newValue === 1) {
      section = props.format === 'AUDIO' ? 'aud' : 'f';
    } else if (newValue === 2) {
      section = 'q';
    } else if (newValue === 3) {
      section = 'ac';
    } else {
      section = 'coe';
    }
    // Update URL for navigation
    if (url[2] === 'a' || url[2] === 'course') {
      history.replaceState(
        null,
        null,
        `/${url[1]}/${url[2]}/${section}/${url[4]}/${url[5]}`
      );
    } else {
      history.replaceState(
        null,
        null,
        `/${url[1]}/${section}/${url[3]}/${url[4]}`
      );
    }
  };

  /**
   * Handles returning to video view and updates URL
   * Used for navigation from quiz/activity/peer evaluation back to video
   */
  const returnVideoHandler = () => {
    if (url[2] === 'a' || url[2] === 'course') {
      history.replaceState(
        null,
        null,
        `/${url[1]}/${url[2]}/f/${url[4]}/${url[5]}`
      );
    } else {
      history.replaceState(
        null,
        null,
        `/${url[1]}/${props.format === 'AUDIO' ? 'aud' : 'f'}/
  ${url[3]}
  /${url[4]}`
      );
    }
    props.changeView(1);
  };

  /**
   * Initial effect to set the view based on URL section
   * Handles deep-linking and direct navigation to lesson sections
   */
  useEffect(() => {
    if (url[2] === 'q' || url[3] === 'q') {
      props.changeView(2);
    } else if (url[2] === 'ac' || url[3] === 'ac') {
      props.changeView(3);
    } else if (url[2] === 'coe' || url[3] === 'coe') {
      props.changeView(4);
    } else if (
      url[2] === 'aud' ||
      url[3] === 'aud' ||
      url[2] === 'lesson' ||
      url[3] === 'lesson'
    ) {
      props.changeView(1);
    }
  }, []); // Runs once on mount

  return (
    <div
      className={
        url[2] !== 'lp'
          ? classes.lessonLeftContainer
          : classes.lessonLeftContainerW
      }
    >
      {/* Main content rendering based on current view and state */}
      {!showMessage ? (
        <>
          {/* Video view: Renders BtnsLessons and VideoLesson if historyMedia is valid */}
          {props.view === 1 && (
            <BtnsLessons
              lessonData={props.lessonInfo.data}
              idLesson={props.idLesson}
              idCourse={props.idCourse}
              loadLesson={props.loadLesson}
              format={props.format}
            />
          )}
          {url[2] !== 'lp' && (
            <Tabs
              value={props.view}
              onChange={handleChange}
              TabIndicatorProps={{
                style: { background: '#890f20' }
              }}
            >
              {/* Tab navigation for lesson sections */}
              <Tab
                className={`${classes.tab} ${
                  props.view === 1 && classes.itemSelected
                }`}
                value={1}
                label={
                  url[2] === 'lesson' || url[3] === 'lesson'
                    ? 'Lección'
                    : props.format === 'AUDIO'
                    ? 'Audio'
                    : t('video')
                }
              />
              <Tab
                className={
                  props.lessonInfo.data.idQuiz > 0 &&
                  props.lessonInfo.data.isShowQuiz
                    ? `${classes.tab} ${
                        props.view === 2 && classes.itemSelected
                      }`
                    : classes.tabNoShow
                }
                value={2}
                label={t('assessment')}
              />
              <Tab
                className={
                  props.lessonInfo.data.hasEvaluacion
                    ? `${classes.tab} ${
                        props.view === 3 && classes.itemSelected
                      }`
                    : classes.tabNoShow
                }
                value={3}
                label={t('activity')}
              />
              <Tab
                className={
                  props.lessonInfo.data.hasEvaluacion
                    ? `${classes.tab} ${
                        props.view === 4 && classes.itemSelected
                      }`
                    : classes.tabNoShow
                }
                value={4}
                label={t('peer_evaluation')}
              />
            </Tabs>
          )}
          {/* VideoLesson: Only renders if historyMedia is a valid integer */}
          {props.view === 1 &&
            Number.isInteger(props.historyMedia) && (
              <VideoLesson
                idCourse={props.idCourse}
                idLesson={props.idLesson}
                videoData={props.lessonInfo}
                changeVideoStatus={props.changeVideoStatus}
                historyMedia={props.historyMedia} // Last media position for resuming video/audio
                transcriptHandler={props.transcriptHandler}
                playerTimeHandler={props.playerTimeHandler}
              />
            )}
          {/* QuizContainer: Renders quiz section */}
          {props.view === 2 && (
            <QuizContainer
              idCourse={props.idCourse}
              idLesson={props.idLesson}
              changeQuizStatus={props.changeQuizStatus}
              returnVideo={returnVideoHandler}
            />
          )}
          {/* Activity: Renders activity section */}
          {props.view === 3 && (
            <Activity
              returnVideo={returnVideoHandler}
              idCourse={props.idCourse}
              idLesson={props.idLesson}
              changeHomeworkStatus={props.changeHomeworkStatus}
            />
          )}
          {/* CoevaluationUsersContainer: Renders peer evaluation section */}
          {props.view === 4 && (
            <CoevaluationUsersContainer
              idCourse={props.idCourse}
              idLesson={props.idLesson}
              changeCoevaluationStatus={
                props.changeCoevaluationStatus
              }
            />
          )}
        </>
      ) : (
        /* Finished course message view */
        <FinishedCourseMessage />
      )}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
LessonLeft.propTypes = {
  /** Lesson data object for current lesson context */
  lessonInfo: PropTypes.object,
  /** Course ID for lesson context and navigation */
  idCourse: PropTypes.number,
  /** Lesson ID for lesson context and navigation */
  idLesson: PropTypes.number,
  /** Current view index (1: video, 2: quiz, 3: activity, 4: peer evaluation) */
  view: PropTypes.number,
  /** Function to change the current view */
  changeView: PropTypes.func,
  /** Function to change video status */
  changeVideoStatus: PropTypes.func,
  /** Function to change quiz status */
  changeQuizStatus: PropTypes.func,
  /** Function to change homework status */
  changeHomeworkStatus: PropTypes.func,
  /** Function to change coevaluation status */
  changeCoevaluationStatus: PropTypes.func,
  /** Function to load lesson data */
  loadLesson: PropTypes.func,
  /** Function to handle transcript updates */
  transcriptHandler: PropTypes.func,
  /** Function to handle player time updates */
  playerTimeHandler: PropTypes.func,
  /** Last media history position for resuming video/audio */
  historyMedia: PropTypes.number,
  /** Format of the lesson (e.g., 'AUDIO', 'VIDEO') */
  format: PropTypes.string
};

LessonLeft.defaultProps = {
  lessonInfo: {},
  idCourse: undefined,
  idLesson: undefined,
  view: 1,
  changeView: undefined,
  changeVideoStatus: undefined,
  changeQuizStatus: undefined,
  changeHomeworkStatus: undefined,
  changeCoevaluationStatus: undefined,
  loadLesson: undefined,
  transcriptHandler: undefined,
  playerTimeHandler: undefined,
  historyMedia: undefined,
  format: undefined
};

export default LessonLeft;
